
import Vue, { PropType } from 'vue'
import { ApexOptions } from 'apexcharts'
import { group } from 'd3-array'

import Plant from '@/models/Plant'
import { dateOnly } from '@/types/models'

import VueApexCharts from 'vue-apexcharts'

type ChartType = 'line'

type Data = { x: dateOnly; y: number }

export default Vue.extend({
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    plant: Object as PropType<Plant>,
  },
  computed: {
    hasData(): boolean {
      return true
    },
    chartType(): ChartType {
      return 'line'
    },
    colors(): string[] {
      return [
        this.$vuetify.theme.currentTheme.primary,
        this.$vuetify.theme.currentTheme.secondary,
        this.$vuetify.theme.currentTheme.accent,
      ]
        .map((v) =>
          typeof v === 'string'
            ? v
            : typeof v === 'number'
            ? v.toString()
            : v === undefined
            ? undefined
            : v.base?.toString()
        )
        .filter((v): v is string => !!v)
    },
    chartOptions(): ApexOptions {
      return {
        chart: {
          zoom: {
            enabled: false,
          },
        },
        colors: this.colors,
        stroke: {
          curve: 'stepline',
        },
        markers: {
          size: 4,
        },
        fill: {
          opacity: 1,
        },
        xaxis: {
          type: 'datetime',
        },
        yaxis: [
          {},
          // { opposite: true }
        ],
      }
    },
    data(): Data[] {
      const harvests = this.plant.harvests.filter((h) => h.quality > 0)

      return Array.from(
        group(harvests, (d) => d.date),
        ([key, value]) => ({ key, value })
      )
        .sort((a, b) => (a.key > b.key ? 1 : -1))
        .map((item) => ({
          x: item.key,
          y: item.value.reduce((sum, h) => sum + h.quantity, 0),
        }))
    },
    cumulative(): Data[] {
      const d = this.data.map((item, i, items) => ({
        x: item.x,
        y: items.slice(0, i).reduce((sum, v) => sum + v.y, 0) + item.y,
      }))

      // append startDate to front
      if (this.plant.startDate && d[0]?.x !== this.plant.startDate) {
        d.unshift({ x: this.plant.startDate, y: 0 })
      }

      // append endDate or today to end
      const endDate =
        this.plant.endDate ?? new Date().toISOString().split('T')[0]
      if (d.slice(-1)[0]?.x !== endDate) {
        d.push(...d.slice(-1).map((v) => ({ x: endDate, y: v.y })))
      }

      return d
    },
    series(): unknown {
      return [
        {
          name: this.plant.name,
          type: 'line',
          data: this.cumulative,
        },
        {
          name: this.plant.name + ' (Daily}',
          type: 'column',
          data: this.data,
        },
      ]
    },
    chartWidth(): string | number {
      return '100%'
    },
    chartHeight(): string | number {
      return 'auto'
    },
  },
})
